import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { getPermissions } from "@/common/Permissions";
import { EmptyState } from "@/components/ui/empty-state";
import {
  classifyFocusedServiceRole,
  type FocusedRolePermissions,
} from "@/components/ui/sidebar/facility/focused-role";
import { usePermissions } from "@/context/PermissionContext";

import Page from "@/components/Common/Page";
import { CardListSkeleton } from "@/components/Common/SkeletonLoading";

import useFilters from "@/hooks/useFilters";
import useCurrentFacility from "@/pages/Facility/utils/useCurrentFacility";

import query from "@/Utils/request/query";
import { ServiceCard } from "@/pages/Facility/settings/healthcareService/ServiceCard";
import {
  HealthcareServiceReadSpec,
  InternalType,
} from "@/types/healthcareService/healthcareService";
import healthcareServiceApi from "@/types/healthcareService/healthcareServiceApi";
import { FolderOpenIcon } from "@heroicons/react/24/outline";

export default function FacilityServicesPage({
  facilityId,
}: {
  facilityId: string;
}) {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const { facility } = useCurrentFacility();
  const { qParams, Pagination, resultsPerPage } = useFilters({
    limit: 12,
    disableCache: true,
  });
  const facilityPermissions = facility?.permissions ?? [];
  const permissions = getPermissions(hasPermission, facilityPermissions);
  const focusedPermissions: FocusedRolePermissions = {
    ...permissions,
    canReadMedicationDispense: hasPermission(
      "read_medication_dispense",
      facilityPermissions,
    ),
    canWriteMedicationDispense: hasPermission(
      "write_medication_dispense",
      facilityPermissions,
    ),
    isPharmacist: hasPermission("is_pharmacist", facilityPermissions),
    canReadSupplyRequest: hasPermission(
      "can_read_supply_request",
      facilityPermissions,
    ),
    canWriteSupplyRequest: hasPermission(
      "can_write_supply_request",
      facilityPermissions,
    ),
    canReadSupplyDelivery: hasPermission(
      "can_read_supply_delivery",
      facilityPermissions,
    ),
    canWriteSupplyDelivery: hasPermission(
      "can_write_supply_delivery",
      facilityPermissions,
    ),
    canReadServiceRequest: hasPermission(
      "can_read_service_request",
      facilityPermissions,
    ),
    canWriteServiceRequest: hasPermission(
      "can_write_service_request",
      facilityPermissions,
    ),
    canReadDiagnosticReport: hasPermission(
      "can_read_diagnostic_report",
      facilityPermissions,
    ),
    canWriteDiagnosticReport: hasPermission(
      "can_write_diagnostic_report",
      facilityPermissions,
    ),
    canReadSpecimen: hasPermission("can_read_specimen", facilityPermissions),
    canWriteSpecimen: hasPermission("can_write_specimen", facilityPermissions),
    canReadProduct: hasPermission("can_read_product", facilityPermissions),
    canWriteProduct: hasPermission("can_write_product", facilityPermissions),
    canReadProductKnowledge: hasPermission(
      "can_read_product_knowledge",
      facilityPermissions,
    ),
    canWriteProductKnowledge: hasPermission(
      "can_write_product_knowledge",
      facilityPermissions,
    ),
    canReadInventoryItem: hasPermission(
      "can_read_inventory_item",
      facilityPermissions,
    ),
    canReadChargeItemDefinition: hasPermission(
      "can_read_charge_item_definition",
      facilityPermissions,
    ),
    canReadResourceCategory: permissions.canReadResourceCategory,
  };
  const focusedServiceRole = classifyFocusedServiceRole(focusedPermissions);
  const focusedInternalType =
    focusedServiceRole === "pharmacy"
      ? InternalType.pharmacy
      : focusedServiceRole === "inventory"
        ? InternalType.store
        : undefined;

  const { data: response, isLoading } = useQuery({
    queryKey: ["healthcareServices", qParams, focusedInternalType],
    queryFn: query(healthcareServiceApi.listHealthcareService, {
      pathParams: { facilityId },
      queryParams: {
        internal_type: focusedInternalType,
        limit: resultsPerPage,
        offset: ((qParams.page ?? 1) - 1) * resultsPerPage,
      },
    }),
  });

  const healthcareServices = (
    (response?.results || []) as HealthcareServiceReadSpec[]
  ).filter((service) => {
    if (focusedServiceRole === "pharmacy") {
      return service.internal_type === InternalType.pharmacy;
    }
    if (focusedServiceRole === "inventory") {
      return service.internal_type === InternalType.store;
    }
    return true;
  });

  return (
    <Page title={t("services")} hideTitleOnPage>
      <div className="container mx-auto">
        <div className="mb-6 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t("services")}
            </h1>
            <p className="mt-1 text-sm text-gray-600">
              {t("discover_healthcare_services")}
            </p>
          </div>
          {/* AayushNet Logo - Right side */}
          <div className="hidden md:flex items-center gap-0 shrink-0">
            <img
              src="/images/aayushnet_logo_light.png"
              className="h-12"
              alt="AayushNet logo"
            />
            <h1
              className="text-2xl font-black tracking-wider"
              style={{
                fontFamily: "'Zen Kaku Gothic Antique', sans-serif",
              }}
            >
              <span
                style={{
                  background:
                    "linear-gradient(to bottom, #5ba3ff 0%, #002d6b 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  display: "inline-block",
                  paddingBottom: "2px",
                }}
              >
                Aayush
              </span>
              <span
                style={{
                  background:
                    "linear-gradient(to bottom, #66ff99 0%, #1a7a3f 100%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  display: "inline-block",
                  paddingBottom: "2px",
                }}
              >
                Net
              </span>
            </h1>
          </div>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            <CardListSkeleton count={4} />
          </div>
        ) : healthcareServices.length === 0 ? (
          <EmptyState
            icon={<FolderOpenIcon className="text-primary size-6" />}
            title={t("no_services_found")}
            description={""}
          />
        ) : (
          <div className="space-y-2">
            {healthcareServices.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                link={`/facility/${facilityId}/services/${service.id}/locations`}
              />
            ))}
          </div>
        )}

        {response && response.count > resultsPerPage && (
          <div className="mt-6 flex justify-center">
            <Pagination totalCount={response.count} />
          </div>
        )}
      </div>
    </Page>
  );
}
